import React, { useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useTheme, Box } from "@mui/material";
import Background from "../Images/background.png"; // Keep your image import
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import StudentSidebar from "./StudentSdebar";

export default function About() {
  const [isImageHovered, setIsImageHovered] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [hoveredCard, setHoveredCard] = useState(null);

  // --- INLINE STYLES ---
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Dynamic Theme Colors
  const THEME_PINK = theme.palette.primary.main;
  const THEME_BLUE = theme.palette.primary.main;
  const THEME_TEXT_PRIMARY = theme.palette.text.primary;
  const THEME_TEXT_SECONDARY = theme.palette.text.secondary;
  const THEME_BACKGROUND_LIGHT = theme.palette.background.default;
  const THEME_CARD_BG = theme.palette.background.paper;
  const THEME_BORDER = theme.palette.divider;

  const styles = {
    pageContainer: {
      fontFamily: "'Poppins', sans-serif",
    },
    imageContainer: {
      position: "relative",
      display: "inline-block",
      width: "100%",
      cursor: "pointer",
      borderRadius: "0.375rem", // Corresponds to Bootstrap's .rounded
      overflow: "hidden", // Ensures overlay respects border radius
    },
    mainImage: {
      width: "100%",
      maxHeight: "600px",
      objectFit: "cover",
      transition: "transform 0.4s ease-in-out",
      transform: isImageHovered ? "scale(1.05)" : "scale(1)",
    },
    imageOverlay: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.6)",
      opacity: isImageHovered ? 1 : 0,
      transition: "opacity 0.4s ease-in-out",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    hoverText: {
      color: "white",
      fontWeight: "bold",
      fontSize: "18px",
      textAlign: "center",
      width: "80%",
      padding: "20px",
      lineHeight: "1.6",
    },
    mainHeading: {
      fontWeight: "bold",
      color: THEME_TEXT_PRIMARY,
      fontSize: "clamp(2rem, 5vw, 2.8rem)", // Responsive font size
    },
    subHeading: {
      fontWeight: "bold",
      color: THEME_PINK,
      textTransform: "uppercase",
      letterSpacing: "1px",
    },
    benefitCard: {
      marginBottom: "1.5rem",
      padding: "1.5rem",
      border: `1px solid ${THEME_BORDER}`,
      borderRadius: "0.5rem",
      boxShadow: isDark
        ? "0 4px 12px rgba(0,0,0,0.5)"
        : "0 4px 12px rgba(0,0,0,0.05)",
      backgroundColor: THEME_CARD_BG,
      transition: "transform 0.3s ease, box-shadow 0.3s ease",
    },
    benefitCardHover: {
      transform: "translateY(-5px)",
      boxShadow: `0 8px 20px ${theme.palette.divider}`,
    },
    cardTitle: {
      fontWeight: "bold",
      color: THEME_BLUE,
      marginBottom: "0.75rem",
    },
    cardText: {
      color: THEME_TEXT_SECONDARY,
      lineHeight: "1.7",
    },
    aboutSection: {
      padding: "2rem",
      backgroundColor: THEME_BACKGROUND_LIGHT,
      border: `1px solid ${THEME_BORDER}`,
      borderRadius: "0.5rem",
      boxShadow: isDark
        ? "0 4px 12px rgba(0,0,0,0.5)"
        : "0 4px 12px rgba(0,0,0,0.05)",
      color: THEME_TEXT_PRIMARY,
    },
    aboutText: {
      fontSize: "16px",
      lineHeight: "1.8",
      textAlign: "justify",
    },
    highlightText: {
      fontWeight: "700",
      color: THEME_PINK,
    },
  };

  const benefits = [
    {
      title: "Flexible & Accessible Learning",
      text: "Access lectures, assignments, and materials anytime, anywhere. Whether you're a working professional or a student, KaaS fits seamlessly into your schedule, empowering you to learn on your terms.",
    },
    {
      title: "Personalized Learning Paths",
      text: "Unlike rigid classroom environments, our platform lets you learn at your own pace. Master difficult concepts by reviewing them multiple times, or accelerate through topics you find easy.",
    },
    {
      title: "Interactive & Engaging Content",
      text: "We use cutting-edge tools like video lessons, interactive quizzes, and real-world projects to make learning practical and engaging. Collaborate in forums and get mentorship to enhance your journey.",
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
        bgcolor: "background.default",
        color: "text.primary",
      }}
    >
      <Header />
      <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
        <StudentSidebar onToggle={setSidebarExpanded} />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: sidebarExpanded ? "calc(100% - 260px)" : "calc(100% - 80px)",
            transition: theme.transitions.create("width", { duration: 300 }),
            height: "calc(100vh - 64px)",
            overflowY: "auto",
          }}
        >
          <div className="container" style={styles.pageContainer}>
            <div className="row align-items-center">
              {/* Left Side - Image & Hover Text */}
              <div className="col-lg-6 col-md-12 text-center mb-4">
                <div
                  style={styles.imageContainer}
                  onMouseEnter={() => setIsImageHovered(true)}
                  onMouseLeave={() => setIsImageHovered(false)}
                >
                  <img
                    src={Background}
                    alt="Online Learning"
                    className="img-fluid rounded shadow"
                    style={styles.mainImage}
                  />
                  <div style={styles.imageOverlay}>
                    <div style={styles.hoverText}>
                      KaaS is your ultimate learning platform, designed to
                      elevate your personal and professional growth with
                      expertly crafted courses.
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side - Text Content */}
              <div className="col-lg-6 col-md-12">
                <div className="text-center text-lg-start">
                  <h4 style={styles.subHeading}>LEARN ANYTHING</h4>
                  <h1 style={styles.mainHeading}>
                    Benefits of Online Learning Expertise
                  </h1>
                </div>
                <div className="mt-4">
                  {benefits.map((benefit, index) => (
                    <div
                      key={index}
                      style={{
                        ...styles.benefitCard,
                        ...(hoveredCard === index
                          ? styles.benefitCardHover
                          : {}),
                      }}
                      onMouseEnter={() => setHoveredCard(index)}
                      onMouseLeave={() => setHoveredCard(null)}
                    >
                      <h3 style={styles.cardTitle}>{benefit.title}</h3>
                      <p style={styles.cardText}>{benefit.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="container py-5">
              <div style={styles.aboutSection}>
                <p style={styles.aboutText}>
                  <span style={styles.highlightText}>KaaS</span> is a premier
                  learning initiative designed to transform professional
                  development in the digital age. With a strong foundation in
                  educational technology and industry expertise, we are pioneers
                  in building sustainable and scalable learning solutions.
                  <br />
                  <br />
                  Our platform provides a comprehensive digital ecosystem
                  focused on in-demand sectors like technology, business, and
                  creative arts. Our purpose is to centralize knowledge, foster
                  innovation, and enhance professional competencies by
                  delivering{" "}
                  <span style={styles.highlightText}>
                    practical, research-backed, and expert-led training programs
                  </span>
                  .
                  <br />
                  <br />
                  <span style={styles.highlightText}>KaaS</span> is more than
                  just a learning platform—it’s a capability-building
                  environment that connects learners with industry leaders.
                  Every course is designed with real-world application in mind,
                  helping individuals upskill, gain certifications, and make
                  meaningful contributions to their fields.
                  <br />
                  <br />
                  We believe in empowering professionals through innovative,
                  expert-led, and technology-driven learning to make skill
                  acquisition simple, effective, and directly applicable to
                  career growth and success.
                </p>
              </div>
            </div>
          </div>
          <Footer />
        </Box>
      </Box>
    </Box>
  );
}
